import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchManagementSections } from '../../../services/managementService';
import { fetchTournamentById, fetchTournaments } from '../../../services/tournamentService';
import { useAuth } from '../../../context/AuthContext';
import { useRoleContext } from '../../../context/RoleContext';
import { getOrganizationId } from '../../../utils/organization';
import { getCommunityId } from '../../../utils/community';
import { getClubId } from '../../../utils/club';
import { TournamentCard } from '../../../components/common/cards';
import PageShell from '../../../components/layout/PageShell';
import {
  buildManagementScope,
  buildMatchManagerFormatsPath,
  buildMatchManagerTournamentsPath,
  buildPlayerManagerTournamentPath,
  buildPlayerManagerTournamentsPath,
  getKnownTournamentId,
} from '../../../utils/managementNavigation';
import { normalizeId } from '../../../utils/ids';

const statusBadgeClass = {
  available: 'badge bg-success-subtle text-success-emphasis',
  coming_soon: 'badge bg-warning-subtle text-warning-emphasis',
};

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const ManagementHubPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId, communityId, clubId, tournamentId } = useParams();
  const { token, user } = useAuth();
  const { activeContext } = useRoleContext();
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [tournament, setTournament] = useState(null);
  const [tournamentError, setTournamentError] = useState('');
  const [isLoadingTournament, setIsLoadingTournament] = useState(false);
  const [assignedTournaments, setAssignedTournaments] = useState([]);
  const [assignedTournamentError, setAssignedTournamentError] = useState('');
  const [isLoadingAssignedTournaments, setIsLoadingAssignedTournaments] = useState(false);

  const normalizedUserId = useMemo(() => normalizeId(user), [user]);
  const userRoles = useMemo(
    () => new Set([...(user?.roles || []), user?.role].filter(Boolean)),
    [user],
  );
  const isManagerUser = useMemo(
    () =>
      ['tournament_manager', 'player_manager', 'auction_manager', 'match_manager'].some((role) =>
        userRoles.has(role),
      ),
    [userRoles],
  );
  const communityScope = useMemo(
    () => communityId || user?.community || '',
    [communityId, user?.community],
  );
  const clubScope = useMemo(
    () => clubId || tournament?.club || user?.club || '',
    [clubId, tournament?.club, user?.club],
  );
  const organizationScope = useMemo(
    () => organizationId || tournament?.organization || user?.organization || '',
    [organizationId, tournament?.organization, user?.organization],
  );
  const normalizedOrganizationId = useMemo(
    () => getOrganizationId(organizationScope),
    [organizationScope]
  );
  const normalizedCommunityId = useMemo(
    () => getCommunityId(communityScope),
    [communityScope]
  );
  const normalizedClubId = useMemo(
    () => getClubId(clubScope),
    [clubScope]
  );
  const activeTournamentId = useMemo(
    () =>
      getKnownTournamentId({
        params: { tournamentId },
        location,
      }),
    [location, tournamentId],
  );
  const managementScope = useMemo(
    () =>
      buildManagementScope({
        organizationId: normalizedOrganizationId,
        communityId: normalizedCommunityId,
        clubId: normalizedClubId,
        entityContext: activeContext,
      }),
    [activeContext, normalizedClubId, normalizedCommunityId, normalizedOrganizationId],
  );

  useEffect(() => {
    const loadSections = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await fetchManagementSections();
        setSections(data.sections || []);
      } catch (err) {
        setError(err.message || 'Unable to load management hub.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSections();
  }, []);

  useEffect(() => {
    const loadTournament = async () => {
      if (!tournamentId || !token) {
        setTournament(null);
        setTournamentError('');
        return;
      }

      setIsLoadingTournament(true);
      setTournamentError('');

      try {
        const response = await fetchTournamentById(tournamentId, token, organizationId, {
          communityId: normalizedCommunityId,
          clubId: normalizedClubId,
        });
        setTournament(response.tournament || null);
      } catch (err) {
        setTournament(null);
        setTournamentError(err.message || 'Unable to load tournament.');
      } finally {
        setIsLoadingTournament(false);
      }
    };

    loadTournament();
  }, [normalizedClubId, normalizedCommunityId, organizationId, token, tournamentId]);

  useEffect(() => {
    const loadAssignedTournaments = async () => {
      if (!token || !isManagerUser || tournamentId) {
        setAssignedTournaments([]);
        setAssignedTournamentError('');
        return;
      }

      setIsLoadingAssignedTournaments(true);
      setAssignedTournamentError('');

      try {
        const response = await fetchTournaments(token, organizationScope, {
          communityId: normalizedCommunityId,
          clubId: normalizedClubId,
        });
        setAssignedTournaments(response.tournaments || []);
      } catch (err) {
        setAssignedTournamentError(err.message || 'Unable to load assigned tournaments.');
      } finally {
        setIsLoadingAssignedTournaments(false);
      }
    };

    loadAssignedTournaments();
  }, [isManagerUser, normalizedClubId, normalizedCommunityId, organizationScope, token, tournamentId]);

  const managerSections = useMemo(() => {
    if (!tournamentId || !tournament || !normalizedUserId) return [];

    const matchesUser = (value) => normalizeId(value) === normalizedUserId;
    const assignedSections = [];

    if (matchesUser(tournament.registrationManager)) {
      assignedSections.push('player');
    }

    if (matchesUser(tournament.auctionManager)) {
      assignedSections.push('auction');
    }

    if (matchesUser(tournament.matchManager)) {
      assignedSections.push('match');
    }

    return assignedSections;
  }, [normalizedUserId, tournament, tournamentId]);

  const filteredSections = useMemo(() => {
    if (!tournamentId) return sections;

    if (managerSections.length === 0) return [];

    return sections.filter((section) => managerSections.includes(section.id));
  }, [managerSections, sections, tournamentId]);

  const assignedTournamentCards = useMemo(() => {
    if (!normalizedUserId || !Array.isArray(assignedTournaments)) return [];

    return assignedTournaments
      .map((tournamentItem) => {
        const registrationManagerId = normalizeId(tournamentItem?.registrationManager);
        const auctionManagerId = normalizeId(tournamentItem?.auctionManager);
        const matchManagerId = normalizeId(tournamentItem?.matchManager);
        const roles = [];

        if (registrationManagerId === normalizedUserId) {
          roles.push('Player manager');
        }

        if (auctionManagerId === normalizedUserId) {
          roles.push('Auction manager');
        }

        if (matchManagerId === normalizedUserId) {
          roles.push('Match manager');
        }

        const tournamentIdValue =
          tournamentItem?.id ||
          tournamentItem?._id?.toString?.() ||
          tournamentItem?.tournamentId ||
          tournamentItem?._id;

        return {
          tournament: tournamentItem,
          tournamentId: tournamentIdValue?.toString?.() || tournamentIdValue,
          roles,
        };
      })
      .filter((item) => item.tournamentId && item.roles.length > 0);
  }, [assignedTournaments, normalizedUserId]);

  const resolveSectionLink = (section) => {
    if (!section?.link) return '';

    let resolvedLink = section.link;

    if (normalizedCommunityId && section.communityLink) {
      resolvedLink = section.communityLink;
    }

    if (normalizedClubId && section.clubLink) {
      resolvedLink = section.clubLink;
    }

    if (normalizedCommunityId) {
      resolvedLink = resolvedLink.replace('/org/:organizationId', '/community/:communityId');
    }

    if (normalizedClubId) {
      resolvedLink = resolvedLink.replace('/org/:organizationId', '/club/:clubId');
    }

    if (normalizedOrganizationId) {
      resolvedLink = resolvedLink.replace(':organizationId', normalizedOrganizationId);
    }

    if (normalizedCommunityId) {
      resolvedLink = resolvedLink.replace(':communityId', normalizedCommunityId);
    }
    if (normalizedClubId) {
      resolvedLink = resolvedLink.replace(':clubId', normalizedClubId);
    }

    if (normalizedUserId) {
      resolvedLink = resolvedLink.replace(':playerManagerId', normalizedUserId);
      resolvedLink = resolvedLink.replace(':auctionManagerId', normalizedUserId);
      resolvedLink = resolvedLink.replace(':managerId', normalizedUserId);
    }

    if (activeTournamentId) {
      resolvedLink = resolvedLink.replace(':tournamentId', activeTournamentId);
    }

    if (resolvedLink.includes(':')) {
      return '';
    }

    return resolvedLink;
  };

  const handleCardClick = (section) => {
    if (section?.id === 'match' && section?.status === 'available') {
      const matchManagerPath = activeTournamentId
        ? buildMatchManagerFormatsPath(managementScope, normalizedUserId, activeTournamentId)
        : buildMatchManagerTournamentsPath(managementScope, normalizedUserId);

      if (matchManagerPath) {
        navigate(matchManagerPath, { replace: false });
        return;
      }
    }

    if (section?.id === 'player' && section?.status === 'available') {
      const playerManagerPath = activeTournamentId
        ? buildPlayerManagerTournamentPath(managementScope, normalizedUserId, activeTournamentId)
        : buildPlayerManagerTournamentsPath(managementScope, normalizedUserId);

      if (playerManagerPath) {
        navigate(playerManagerPath, {
          replace: false,
          state: activeTournamentId ? { tournamentId: activeTournamentId } : undefined,
        });
        return;
      }
    }

    const resolvedLink = resolveSectionLink(section);

    if (section.status === 'available' && resolvedLink) {
      navigate(resolvedLink, { replace: false });
      return;
    }

    navigate(`/management/${section.id}/coming-soon`, { state: section });
  };

  return (
    <div className="py-4">
      <div className="mb-4 d-flex flex-column gap-2">
        <p className="eyebrow text-uppercase text-primary fw-semibold mb-0">Management Hub</p>
        <h1 className="h3 mb-1">Choose a management module</h1>
        <p className="text-muted mb-0">
          Jump into player, auction, or match management and keep your tournaments on track.
        </p>
      </div>

      {!tournamentId && isManagerUser && (
        <div className="mb-4">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2 mb-3">
            <div>
              <h2 className="h5 mb-1">Your assigned tournaments</h2>
              <p className="text-muted mb-0">Open a tournament to access the tools assigned to you.</p>
            </div>
          </div>

          {assignedTournamentError && (
            <div className="alert alert-danger" role="alert">
              {assignedTournamentError}
            </div>
          )}

          {isLoadingAssignedTournaments && (
            <div className="alert alert-secondary mb-0">Loading assigned tournaments...</div>
          )}

          {!isLoadingAssignedTournaments && assignedTournamentCards.length === 0 && !assignedTournamentError && (
            <div className="alert alert-info mb-0">No tournaments are assigned to you yet.</div>
          )}

          {!isLoadingAssignedTournaments && assignedTournamentCards.length > 0 && (
            <div className="row g-3">
              {assignedTournamentCards.map(({ tournament, tournamentId: tournamentIdValue, roles }) => {
                const tournamentOrgId = getOrganizationId(tournament?.organization);
                const tournamentLink =
                  tournamentOrgId && tournamentIdValue
                    ? `/org/${tournamentOrgId}/managers/tournaments/${tournamentIdValue}`
                    : '';

                return (
    <PageShell containerClassName="p-0">
                  <div className="col-12 col-lg-6" key={tournamentIdValue}>
                    <TournamentCard
                      tournament={{ ...tournament, entityName: tournament?.organization?.name }}
                      onClick={() => {
                        if (tournamentLink) {
                          navigate(tournamentLink);
                        }
                      }}
                      onManage={() => {
                        if (tournamentLink) {
                          navigate(tournamentLink);
                        }
                      }}
                      showActions
                    />
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {roles.map((role) => (
                        <span key={role} className="badge bg-primary-subtle text-primary-emphasis">
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                
    </PageShell>
  );
              })}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {tournamentError && (
        <div className="alert alert-danger" role="alert">
          {tournamentError}
        </div>
      )}

      <div className="row g-4">
        {(isLoading || isLoadingTournament) && (
          <div className="col-12">Loading management modules...</div>
        )}
        {!isLoading && !isLoadingTournament && filteredSections.length === 0 && tournamentId && (
          <div className="col-12">
            <div className="alert alert-info mb-0">
              {tournament
                ? 'You are not assigned to any management role for this tournament.'
                : 'Management roles for this tournament could not be determined.'}
            </div>
          </div>
        )}
        {!isLoading &&
          !isLoadingTournament &&
          filteredSections.map((section) => (
            <div className="col-12 col-lg-4" key={section.id}>
              <div
                className="card shadow-sm h-100 border-0 cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => handleCardClick(section)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleCardClick(section);
                  }
                }}
              >
                <div className="card-body d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <p className="eyebrow text-uppercase text-primary fw-semibold mb-1">{section.id}</p>
                      <h2 className="h5 mb-1">{section.title}</h2>
                      <p className="text-muted mb-0">{section.description}</p>
                    </div>
                    <span className={statusBadgeClass[section.status] || 'badge bg-secondary-subtle text-secondary-emphasis'}>
                      {section.status === 'available' ? 'Available' : 'Coming soon'}
                    </span>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    <span className="btn btn-sm btn-primary">
                      {section.status === 'available' ? 'Open workspace' : 'Preview'}
                    </span>
                    <span className="text-muted small">
                      Click to {section.status === 'available' ? 'launch this workspace' : 'view status'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default ManagementHubPage;
